import { useState } from 'react';
import type { BoxProps } from '@mantine/core';
import { useCanChooseDefaultTemplate } from 'src/data-capture/hooks/templates/useCanChooseDefaultTemplate';
import type { TemplateWithoutSections } from 'src/data-capture/hooks/templates/useTemplates';
import { Button } from 'src/design-system/Button';
import { ChooseDefaultTemplateModal } from './ChooseDefaultTemplateModal';
import { DefaultTemplateNotice } from './DefaultTemplateNotice';

type Props = BoxProps & {
  templates: TemplateWithoutSections[];
};

/** Tells people automatic notes are paused because the firm has several templates and no default. */
export function NoDefaultPrompt({ templates, ...boxProps }: Props) {
  const canChooseDefault = useCanChooseDefaultTemplate();
  const [isChoosing, setIsChoosing] = useState(false);

  if (!canChooseDefault) {
    return (
      <DefaultTemplateNotice tone="warning" {...boxProps}>
        Automatic notes are paused until a system admin at your firm chooses a default template.
      </DefaultTemplateNotice>
    );
  }

  return (
    <>
      <DefaultTemplateNotice
        tone="warning"
        title="Choose a default template"
        actions={
          <Button variant="text" onClick={() => setIsChoosing(true)}>
            Choose a default
          </Button>
        }
        {...boxProps}
      >
        {
          "We can't write notes automatically until your firm has a default. Pick the template we should use after every recorded meeting."
        }
      </DefaultTemplateNotice>
      <ChooseDefaultTemplateModal
        opened={isChoosing}
        onClose={() => setIsChoosing(false)}
        templates={templates}
      />
    </>
  );
}
